<script setup>
import { computed, onMounted, ref, watchEffect } from "vue";
import RequiredInformations from "@/components/shared/RequiredInformations.vue";
import { useStore } from "vuex";
import {
  citizenEvent,
  formatDate,
  getResource,
  getDaySlots,
  getMonths,
  lockReservation,
  getServices,
  updateQueryParams,
  getMonthsArray,
  getReservationDetailById,
  updateSlots,
  getLoggedStatus,
  getMonthsObjectArray,
  validateIdentifyValues,
  baseUrl
} from "@/api/httpApi.js";
import CardErogazione from "./CardErogazione.vue";
import CardResource from "../shared/CardResource.vue";
import SelectService from "./SelectService.vue";
import SelectMonth from "./SelectMonth.vue";
import RadioDays from "./RadioDays.vue";
import RadioHours from "./RadioHours.vue";
import CheckboxHours from "./CheckboxHours.vue";
import { showFormToast, validateForm, removeYupFieldsByPrefix } from "@/utility";
import * as Yup from "yup";
import InputValidateIdentify from "@/components/Step2Dates/InputValidateIdentify.vue";

const store = useStore();
const emit = defineEmits(["previous", "next", "goto"]);
const informations = ref(["Appuntamenti disponibili", "Servizio"]);
const resource = ref();
const services = ref();
const monthsDates = ref([]);
const selectedYear = ref(store.state.year || "");
const selectedMonth = ref(store.state.month || "");
const selectedDay = ref(store.state.day || "");
const selectedHour = ref(store.state.hour || undefined);
const selectedSlots = ref(store.state.slots || []);
const selectedService = ref(store.state.serviceId || "");
const serviceSlotNumber = ref(store.state.serviceSlotNumber || 1);
const identifyValues = ref(store.state.identifyValues || []);
const identifyValuesError = ref([]);
const identifyMaxChars = ref(30);
const selectedNumberOfReservations = ref(store.state.numberOfReservations || 1);
const isRecapping = ref(store.state.isRecapping || undefined);
const reservationIdDetails = ref();
const name = ref(store.state.user?.firstName || "");
const surname = ref(store.state.user?.lastName || "");
const fiscalCode = ref(store.state.user?.fiscalCode || "");
const phone = ref(store.state.user?.phone || "");
const mobilePhone = ref(store.state.user?.mobilePhone || "");
const email = ref(store.state.user?.email || "");
const isLoggedIn = ref(false);
const userData = ref(undefined);
const daySlots = ref();
const currentDate = ref(new Date());
const step2Form = ref();
const daysFieldInvalid = ref(false);
const hoursFieldInvalid = ref(false);
const modalRef = ref(null);
const modalErrorMessages = ref([]);

onMounted(async () => {
  if (store.state.reservationId) {
    try {
      reservationIdDetails.value = await getReservationDetailById(
        store.state.reservationId
      );

      selectedSlots.value = reservationIdDetails?.value.reservationSlots;
      selectedDay.value = reservationIdDetails?.value.reservationDate;
    } catch (err) {
      console.error(err);
    }
  }
  await createStep2Form();
});

async function refreshSlots(selectedMonth, selectedYear) {
  if (selectedMonth === "" || selectedYear === "") {
    return;
  }

  const selectedMonthDate = monthsDates.value?.find((month) => month.date.getMonth() == selectedMonth && month.date.getFullYear() == selectedYear) || currentDate;
  const startDate = formatDate(
    new Date(selectedMonthDate.date.getFullYear(), parseInt(selectedMonth), 1)
  );
  const endDate = formatDate(
    new Date(selectedMonthDate.date.getFullYear(), parseInt(selectedMonth) + 1, 0)
  );

  try {
    const payload = {
      resourceId: store.state.resourceId,
      startDate,
      endDate,
      erogationMode:
        resource.value.erogationMode === "mis"
          ? store.state.erogationMode
          : resource.value.erogationMode,
      serviceSlotNumber: serviceSlotNumber.value,
    };
    const updatedPayload = store.state.reservationId
      ? { ...payload, reservationId: store.state.reservationId }
      : payload;

    daySlots.value = await getDaySlots(updatedPayload);
  } catch (error) {
    console.error(error);
  }
}

async function refreshMonths() {

  // crea array dei mesi disponibili per la risorsa scelta
  const monthsArray = getMonthsArray(
    new Date(resource.value.availabilityStartDate),
    new Date(resource.value.availabilityEndDate)
  );

  // array payload necessario per effettuare la chiamata api per avere l'elenco dei mesi con lo status
  const payload = [];
  for (let i = 0; i < monthsArray.length; i++) {
    let sDate;

    /* Nel caso in cui il mese sia quello corrente considero come data di partenza il giorno corrente mentre la data di fine rimane la data di fine mese */
    if((monthsArray[i].getMonth() === currentDate.value.getMonth()) && (monthsArray[i].getFullYear() === currentDate.value.getFullYear())) {
      sDate = currentDate.value.toISOString().slice(0,10);
    } else {
      sDate = formatDate(
        new Date(monthsArray[i].getFullYear(), monthsArray[i].getMonth(), 1)
      );
    }

    const eDate = formatDate(
      new Date(monthsArray[i].getFullYear(), monthsArray[i].getMonth() + 1, 0)
    );

    payload.push({
      startDate: sDate,
      endDate: eDate,
      reservationId: undefined,
      serviceSlotNumber: serviceSlotNumber.value,
      erogationMode: resource.value.erogationMode === "mis" ? store.state.erogationMode : resource.value.erogationMode,
    });
  }

  const monthsAvalability = await getMonths(store.state.resourceId, payload); // chiamata api per recuperare l'elenco dei mesi con lo status

  monthsDates.value = getMonthsObjectArray(monthsAvalability); // crea array dei mesi con data primo giorno del mese e status

  if (selectedMonth.value && selectedYear.value) {
    await refreshSlots(selectedMonth.value, selectedYear.value);
  } else {

    selectedMonth.value = monthsDates.value[0]?.date.getMonth();
    selectedYear.value = monthsDates.value[0]?.date.getFullYear();

    await refreshSlots(selectedMonth.value, selectedYear.value);
  }
}

async function onSelectedErogationMode(erogationMode) {
  store.commit("setErogationMode", erogationMode);
  selectedService.value = "";
  selectedDay.value = "";
  selectedHour.value = "";
  selectedSlots.value = [];
  await updateServiceSlotNumber();
  await refreshMonths();
}

async function onSelectedService(value) {

  selectedService.value = value;
  selectedNumberOfReservations.value = 1;
  selectedDay.value = "";
  selectedHour.value = "";
  selectedSlots.value = [];

  await updateServiceSlotNumber();

  if(resource.value?.isInAllowedList) {
    await setClearIdentifyValues();
  }

  if(resource.value.isInAllowedList && identifyValues.value.length > 0) {
    await addIdentifyValidation();
  }

  await refreshMonths();
  await refreshSlots(selectedMonth.value, selectedYear.value);


}

// funzione che salva sullo store locale il numero di slot per il motivo selezionato
async function updateServiceSlotNumber() {
  serviceSlotNumber.value = services.value?.find(
    (service) => selectedService.value === service.serviceId
  )?.slotsNumber || 1;
}

async function setClearIdentifyValues() {

  // se seleziono un motivo salvo nello store un array con il numero di slot altrimenti svuoto
  if(selectedService.value.length > 0) {

    const filledValues = identifyValues.value.filter(v => v !== '');

    identifyValues.value = Array.from(
      { length: serviceSlotNumber.value },
      (_, i) => filledValues[i] ?? ''
    );

  } else {
    identifyValues.value = [];
  }
}

// Aggiungo alla validazione i campi di input per gli identificativi da controllare nella lista
async function addIdentifyValidation() {

  if(step2Form.value) {
    step2Form.value = await removeYupFieldsByPrefix(step2Form.value, 'identifyValues');

    const newObj = {};
    const errorLabel = {
        KO_NOT_EXIST: "L'identificativo non soddisfa i requisiti necessari per questo servizio",
        KO_EXIST_RESERVATION: "Una prenotazione attiva è già associata all'identificativo",
        KO_DUPLICATED: "Sono stati inseriti uno o più identificativi duplicati"
      }

    for (let i = 1; i <= serviceSlotNumber.value; i++) {
      const nomeCampo = 'identifyValues' + i;
      newObj[nomeCampo] = Yup.string().required("Campo obbligatorio").max(identifyMaxChars.value, `L'identificativo non può superare i ${identifyMaxChars.value} caratteri`).test(
        'identify-validation-' + i,
        '',
        function (value) {
          const apiResult = identifyValuesError.value[value] ?? null;

          if (!apiResult) return true; // nessun errore dall'API

          if (apiResult === 'KO_NOT_EXIST') {
            return this.createError({
              message: errorLabel[apiResult]
            });
          }

          if (apiResult === 'KO_EXIST_RESERVATION') {
            return this.createError({
              message: errorLabel[apiResult]
            });
          }

          if (apiResult === 'KO_DUPLICATED') {
            return this.createError({
              message: errorLabel[apiResult]
            });
          }

          return true;
        }
      );
    }
    step2Form.value = step2Form.value.shape(newObj);
  }
}


// funzione di validazione degli identificativi tramite chiamata api
async function checkIdentifyValues() {
  // rimuovo valori vuoti prima di chiamare la api
  const filteredValues = await identifyValues.value; //.filter(v => v && v.trim() !== '');

  if(filteredValues.length > 0) {
    const reservationId = (store.state.isEditing) ? store.state.reservationId : null;
    const res = await validateIdentifyValues(store.state.resourceId, store.state.serviceId, filteredValues, reservationId);
    const data = res?.data ?? [];

    await new Promise(resolve => {
      identifyValuesError.value = Object.assign({}, ...data);
      resolve();
    });
  }
}

async function resetAndRefresh() {
  selectedDay.value = "";
  selectedHour.value = "";
  selectedSlots.value = [];
  await refreshSlots(selectedMonth.value, selectedYear.value);
}

async function onSelectedDay() {
  selectedHour.value = "";
  selectedSlots.value = [];
}

async function onSelectedHour(value) {
  selectedHour.value = value.startTime;
  selectedSlots.value = [value];
}

async function onSelectedCheckboxHour(slots) {
  selectedHour.value = slots[0]?.startTime;
  selectedSlots.value = slots?.length ? slots : [];
}

const isServiceActive = computed(() => {
  return services.value?.length > 0;
});
const doesServiceDetermines = computed(() => {
  return isServiceActive.value && resource.value?.enableSlotDuration;
});
const requiredNumberSlotsHours = computed(() => {

  return !resource.value?.enableSlotDuration && resource.value?.multiSlotNumber > 0;
});
const slotsNumber = computed(() => {
  if (!isServiceActive.value || !doesServiceDetermines.value)
    return resource.value?.multiSlotNumber || 1;
  return selectedService.value
    ? serviceSlotNumber.value
    : -1;
});

async function createStep2Form() {
  // Schema di validazione - libreria Yup
  step2Form.value = Yup.object().shape({
    selectService: Yup.string().when([], {
      is: () => isServiceActive.value && doesServiceDetermines.value,
      then: (schema) =>  schema.required("Campo obbligatorio"),
      otherwise: (schema) => schema.nullable()
    }),
    days_group: Yup.string().required('Campo obbligatorio'),
    hours_group: Yup.array().when("days_group", {
      is: (value) => value.length > 0,
      then: (schema) => {
        const minFasce = requiredNumberSlotsHours.value ? 1 : slotsNumber.value;
        const txtMinFasce = minFasce + ' ' + ((minFasce > 1) ? 'fasce orarie' : 'fascia oraria');
        const txtMaxFasce = slotsNumber.value + ' ' + ((slotsNumber.value > 1) ? 'fasce orarie' : 'fascia oraria');
        return schema.min(minFasce, 'Devi selezionare almeno ' + txtMinFasce).max(slotsNumber.value, 'Devi selezionare massimo ' + txtMaxFasce).required("Campo obbligatorio");
      },
      otherwise: (schema) => schema.nullable()
    })
  });
}

watchEffect(async () => {
  if (
    step2Form.value &&
    store.state.resourceId &&
    (!resource.value || resource.value.resourceId !== store.state.resourceId)
  ) {
    try {
      const resourceData = await getResource(store.state.resourceId);
      resource.value = resourceData;

      if(resource.value.isInAllowedList && identifyValues.value.length > 0) {
        await addIdentifyValidation();
      }

      if (resource.value.reservationPerUser === 1) {
        selectedNumberOfReservations.value = 1;
      }

      // Primo recupero dei mesi all'avvio dello step
      await refreshMonths();

      if (resourceData?.loginRequired) {
        await (async () => {
          const data = await getLoggedStatus();
          userData.value = data;
          isLoggedIn.value = data.roles.includes("cittadino");
          if (data.roles.includes("cittadino")) {
            name.value = data.firstName || "";
            surname.value = data.lastName || "";
            fiscalCode.value = data.fiscalCode || "";
            email.value = data.email || "";
          }
        })();
      } else {
        isLoggedIn.value = true;
      }

      await updateQueryParams([]);
    } catch (err) {
      console.error(err);
      resource.value = undefined;
    }
  }
});

watchEffect(async () => {
  if (store.state.resourceId && !services.value) {
    try {
      services.value = await getServices(store.state.resourceId);
    } catch (err) {
      console.error(err);
      services.value = undefined;
    }
  }
});

watchEffect(async () => {
  store.commit("setNumberOfReservations", selectedNumberOfReservations.value);
  store.commit("setServiceId", selectedService.value);
  store.commit("setServiceSlotNumber", serviceSlotNumber.value);
  store.commit("setIdentifyValues", identifyValues.value);
  store.commit("setYear", selectedYear.value);
  store.commit("setMonth", selectedMonth.value);
  store.commit("setDay", selectedDay.value);
  store.commit("setHour", selectedHour.value);
  store.commit("setSlots", selectedSlots.value);
});

function incrementaMese(data) {
  // Splitta la stringa di data in anno, mese e giorno
  let [anno, mese, giorno] = data.split('-').map(Number);

  // Incrementa il mese di 1
  mese++;

  // Se il mese è 12 (dicembre), lo resettiamo a 1 (gennaio) e incrementiamo l'anno
  if (mese > 12) {
    mese = 1;
    anno++;
  }

  // Aggiungiamo uno zero davanti al mese e al giorno se necessario
  mese = mese < 10 ? '0' + mese : mese;
  giorno = giorno < 10 ? '0' + giorno : giorno;

  // Restituiamo la data nel formato "YYYY-MM-DD"
  return `${anno}-${mese}-${giorno}`;
}

async function onBack() {
  await updateQueryParams([{key: "resourceId", value: store.state.resourceId}]);
  await updateQueryParams([
    {key: "resourceId", value: store.state.resourceId},
    ...(store.state.officeId
      ? [{key: "officeId", value: store.state.officeId}]
      : []),
    ...(store.state.topicId
      ? [{key: "resourceId", value: store.state.topicId}]
      : []),
    ...(store.state.searchText
      ? [{key: "searchText", value: store.state.searchText}]
      : []),
  ]);
  store.commit("setNumberOfReservations", 1);
  store.commit("setYear", undefined);
  store.commit("setMonth", undefined);
  store.commit("setDay", undefined);
  store.commit("setHour", undefined);
  store.commit("setSlots", []);
  store.commit("setServiceId", undefined);
  store.commit("setDetail", "");
  store.commit("setAttributes", {});
  store.commit("setIdentifyValues", []);
  emit("previous");
}

function setCookie() {
  const d = new Date();
  d.setTime(d.getTime() + 10 * 60 * 1000); // 10 minutes
  window.document.cookie = `should_redirect_plugin_reservation=true;expires=${d.toUTCString()};path=/`;
}

async function nextStep(autoSubmit, goTo = false) {

  // Effettuo la chiamata api per la validazione degli identificativi e li aggiungo all'oggetto da validare
  // Nota: si esegue prima della validazione del form con yup in modo da evitare problemi di asincronismo
  if(resource.value?.isInAllowedList && identifyValues.value.length > 0) {
    if(autoSubmit) {
      await checkIdentifyValues();
    }
  }

  setTimeout(() => {
    const objToValidate = {
      selectService: selectedService.value,
      days_group : selectedDay.value,
      hours_group : selectedSlots.value
    };

    if(resource.value?.isInAllowedList && identifyValues.value.length > 0) {
      identifyValues.value.forEach((value, index) => {
        objToValidate['identifyValues' + (index + 1)] = value;
      });
    }

    validateForm(step2Form.value, 'step2Form', objToValidate, () => {
      if(autoSubmit && goTo) {
        onSave();
      } else if(autoSubmit) {
        // se tutto è valido procedo allo step successivo
        formValid();
      }
    }, (errors) => {
      // in presenza di errori di validazione mostro il toast di errore
      showFormToast('step2Form', errors);
    }, () => {
      // dopo la prima validazione resto in ascolto di ogni cambiamento di input per rieseguire una validazione in tempo reale
      nextStep(false);
    });
  }, 500);
}

async function formValid() {
  try {

    let res = '';

    if (!store.state.isEditing && !store.state.reservationId) {

      await citizenEvent(
        {
          message: "Utente conferma data e ora prenotazione",
          stepId: 2,
          stepType: "intermedio",
          resourceId: store?.state?.resourceId ?? ''
        }
      );

      // chiamata per bloccare gli slot per una prenotazione
      await lockReservation({
        erogationMode:
          resource.value.erogationMode === "mis"
            ? store.state.erogationMode
            : resource.value.erogationMode,
        reservationNumber: store.state.numberOfReservations,
        reservationSlots: store.state.slots,
        resourceId: store.state.resourceId,
        ...(selectedService.value ? { serviceId: selectedService.value } : {}),
      });

    } else if(store.state.isEditing || store.state.reservationId) {

      await citizenEvent(
        {
          message: "Utente cambia data e ora prenotazione",
          stepId: 2,
          stepType: "intermedio",
          resourceId: store?.state?.resourceId ?? ''
        }
      );

      // chiamata di aggiornamento prenotazione
      await updateSlots({
        erogationMode:
          resource.value.erogationMode === "mis"
            ? store.state.erogationMode
            : resource.value.erogationMode,
        reservationNumber: store.state.numberOfReservations,
        reservationSlots: store.state.slots,
        resourceId: store.state.resourceId,
        serviceId: selectedService.value,
        reservationId: store.state.reservationId,
      });
    }

    setCookie();
    emit("next");

  } catch (err) {
    const errors = err.response?.data?.result?.errors;

    if (errors?.length) {
      errors[0].message =
        errors[0].code === 'MX_POS'
          ? "L'orario scelto non è più disponibile.\nTi invitiamo a selezionare un altro orario per completare la prenotazione."
          : errors[0].message;

      modalErrorMessages.value = errors;
    }

    modalRef.value = new window.bootstrap.Modal(
      document.getElementById("errorReservationIdModal")
    );
    modalRef.value.show();
    console.error(err);
  }
}

async function onSave() {

  await citizenEvent(
    {
      message: "Utente cambia data e ora prenotazione",
      stepId: 2,
      stepType: "intermedio",
      resourceId: store?.state?.resourceId ?? ''
    }
  );

  try {
    await updateSlots({
      erogationMode:
        resource.value.erogationMode === "mis"
          ? store.state.erogationMode
          : resource.value.erogationMode,
      reservationNumber: store.state.numberOfReservations,
      reservationSlots: store.state.slots,
      resourceId: store.state.resourceId,
      serviceId: selectedService.value,
      reservationId: store.state.reservationId,
    });
    setCookie();
    emit("goto", 4);
  } catch (err) {
    console.error(err);
  }
}

const isFiscalCodeRequired = (resource) =>
  resource?.requiredFields?.includes("FISCAL_CODE");
const isPhoneRequired = (resource) =>
  resource?.requiredFields?.includes("PHONE");
const isMobilePhoneRequired = (resource) =>
  resource?.requiredFields?.includes("MOBILE_PHONE");

watchEffect(() => {
  if (resource.value?.loginRequired && isLoggedIn.value) {
    store.commit("patchUser", {
      firstName: name.value,
      lastName: surname.value,
      email: email.value,
      ...(isFiscalCodeRequired(resource.value)
        ? { fiscalCode: fiscalCode.value }
        : {}),
      ...(isPhoneRequired(resource.value) ? { phone: phone.value } : {}),
      ...(isMobilePhoneRequired(resource.value)
        ? { mobilePhone: mobilePhone.value }
        : {}),
    });
  }
});
</script>


<style>
.error-message {
  white-space: pre-line;
}
</style>

<template>
  <form id="step2Form" @submit.prevent>
    <div class="container-fluid row mx-auto p-2">
      <div class="col-3 border-end pe-0 d-none d-md-block">
        <RequiredInformations :informations="informations" :progress="40" />
      </div>
      <div class="col-12 px-0 ps-md-5 pe-md-2 mb-5 pt-4 col-md-9">
        <CardErogazione
          v-if="resource && resource.erogationMode === 'mis'"
          @change="
          (value) => {
            onSelectedErogationMode(value);
          }
        "
        />
        <div
          class="bg-light p-4 shadow-sm"
          :id="encodeURIComponent('Appuntamenti disponibili')"
        >
          <h2 class="h3 mb-3">{{`Appuntamenti disponibili${isServiceActive && doesServiceDetermines ? "*" : ""}`}}</h2>
          <p class="mb-5 mt-0" v-if="!isLoggedIn">
            Per questo servizio è richiesto un accesso SPID o CIE
            <a
              :href="`${baseUrl}/wso2silfi/connect/cittadino?destination=/${store.state.appRoute}`"
            >Accedi</a
            >
          </p>
          <SelectService
            :id="`selectService`"
            :name="`selectService`"
            v-if="isServiceActive && doesServiceDetermines"
            :serviceSlotNumber="serviceSlotNumber"
            :services="services"
            :selectedService="selectedService"
            :selectedSlotsNumber="selectedSlots?.length || 0"
            :dataYupLabel="`Motivo`"
            :ariaControls="resource?.isInAllowedList ? 'identify-values-fields' : null"
            :ariaExpanded="resource?.isInAllowedList ? (selectedService.length > 0) : null"
            :ariaLabelledby="resource?.isInAllowedList ? 'identify-values-label' : null"
            @change="
            (value) => {
              onSelectedService(value);
            }
          "
          />

          <template v-if="resource?.isInAllowedList && selectedService.length > 0">
            <div id="identify-values-fields" aria-live="polite">
              <h2 id="identifyValuesLabel" class="visually-hidden">
                Campi di identificazione
              </h2>
              <inputValidateIdentify
                :id="`identifyValues`"
                :name="`identifyValues`"
                :numbersOfIdentify="serviceSlotNumber"
                :identifyValuesLabel="resource?.listType || ''"
                :values="identifyValues"
                :maxChars="identifyMaxChars"
                @validate="
                (res) => {
                  identifyValues = res.values;
                }"
              />
            </div>
          </template>

          <div v-if="!isServiceActive && slotsNumber > 1 && !doesServiceDetermines">
            <span class="mb-2" v-if="slotsNumber === 1">Per il servizio che hai selezionato puoi abilitare una fascia oraria</span>
            <span class="mb-2" v-if="slotsNumber > 1">Per il servizio che hai selezionato puoi abilitare fino a {{ slotsNumber }} fasce orarie consecutive</span>
            <br />
            <span class="mb-5">Selezionati {{ selectedSlots?.length || 0 }}</span>
          </div>

          <SelectMonth
            v-if="
            !isServiceActive ||
            (isServiceActive && !doesServiceDetermines) ||
            (isServiceActive && selectedService)
            "
            :monthsDates="monthsDates"
            :selectedMonth="selectedMonth"
            :selectedYear="selectedYear"
            @change="
            (value) => {
              const dt = new Date(incrementaMese(value));

              selectedYear = parseInt(dt.getFullYear());
              selectedMonth = parseInt(dt.getMonth());

              resetAndRefresh();
            }"
          />
          <div
            class="select-wrapper mt-5"
            v-if="resource?.reservationPerUser > 1"
          >
            <label for="selectNumberOfReservations">Seleziona posti da prenotare</label>
            <select
              id="selectNumberOfReservations"
              v-model="selectedNumberOfReservations"
              @change="resetAndRefresh()"
            >
              <option
                v-for="number in Array.from(
                { length: resource.reservationPerUser },
                (_, i) => i + 1
              )"
                :key="number"
                :value="number">
                {{ number }}
              </option>
            </select>
          </div>
          <div
            class="row"
            v-if="
            (selectedMonth || selectedMonth === 0) &&
            (selectedYear || selectedYear === 0) &&
            (!isServiceActive ||
            (isServiceActive && !doesServiceDetermines) ||
            (isServiceActive && selectedService))">

            <div class="col-12 m-2">
              <span>Scegli il giorno e la fascia oraria tra quelle disponibili per la giornata selezionata, il numero tra parentesi indica i posti disponibili</span>
            </div>

            <div class="col-12 col-xl-9 p-2">
              <RadioDays
                :id="`step2Form_days_radio_group`"
                :name="`days_group`"
                :selectedDay="selectedDay"
                :selectedMonth="selectedMonth"
                :selectedYear="selectedYear"
                :daySlots="daySlots"
                :isValid="!daysFieldInvalid"
                @change="(value) => {
                  selectedDay = value;
                  onSelectedDay();
                }"/>
            </div>
            <div class="col-12 col-xl-3 p-2">
                <RadioHours
                  v-if="selectedDay && slotsNumber === 1"
                  :id="`step2Form_hours_radio_group`"
                  :name="`hours_group`"
                  :daySlots="daySlots"
                  :selectedDay="selectedDay"
                  :selectedHour="selectedHour"
                  :selectedNumberOfReservations="selectedNumberOfReservations"
                  :isValid="!hoursFieldInvalid"
                  @change="onSelectedHour"
                />
                <CheckboxHours
                  v-if="selectedDay && slotsNumber > 1"
                  :id="`step2Form_hours_checkbox_group`"
                  :name="`hours_group`"
                  :daySlots="daySlots"
                  :selectedDay="selectedDay"
                  :selectedSlots="selectedSlots"
                  :selectedNumberOfReservations="selectedNumberOfReservations"
                  :slotsNumber="slotsNumber"
                  :isValid="!hoursFieldInvalid"
                  @change="(slots) => {
                  onSelectedCheckboxHour(slots);
                  }"
                />
            </div>
          </div>
        </div>
        <CardResource :resource="resource" :id="encodeURIComponent('Servizio')" />
        <div
          class="bg-light p-4 shadow-sm mt-5 d-flex flex-row gap-2 align-items-center"
        >
          <svg
            class="icon icon-sm align-self-start icon-danger icon-color"
            aria-hidden="true"
          >
            <use
              :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-warning-circle`"
            ></use>
          </svg>
          <span
          >Cliccando sul pulsante Avanti, avrai a disposizione 5 minuti per
          finalizzare la prenotazione</span
          >
        </div>
        <div class="d-flex justify-content-between mt-4">

          <button
            type="button"
            class="btn btn-outline-primary"
            @click="onBack">
            {{ isRecapping ? "Annulla prenotazione" : "Indietro" }}
          </button>

          <button
            v-if="isRecapping"
            type="button"
            class="btn btn-outline-primary"
            @click="nextStep(true, true)">
            Salva
          </button>

          <button
            type="submit"
            class="btn btn-outline-primary"
            @click="nextStep(true)">
            Avanti
          </button>
        </div>
      </div>
    </div>
  </form>

  <div
    class="modal fade"
    tabindex="-1"
    role="dialog"
    id="errorReservationIdModal"
    aria-labelledby="errorReservationIdModalTitle"
  >
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header flex-column align-items-start">
          <h2 class="modal-title h5" id="errorReservationIdModalTitle">
            Errore Prenotazione:
          </h2>
          <div class="modal-body">
            <ul class="p-0">
              <li v-for="(item, index) in modalErrorMessages" :key="index" class="error-message">{{item.message}}</li>
            </ul>
          </div>
        </div>
        <div class="modal-footer">
          <button
            class="btn btn-primary btn-sm"
            data-bs-dismiss="modal"
            type="button"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
